import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MatchesList from '../components/MatchesList';
import Seo from '../components/Seo';
import { breadcrumbSchema } from '../utils/structuredData';

import '../styles/Matches.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatDateToISO } from '../utils/formatters';

const CompletedMatches = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = localStorage.getItem('completedMatchesDate');
    return savedDate ? new Date(savedDate) : null;
  });

  useEffect(() => {
    const loadMatches = async () => {
      setIsLoading(true);
      try {
        const date = formatDateToISO(selectedDate);
        const url = date
          ? `/api/rl/public/completed-matches?date=${encodeURIComponent(date)}`
          : '/api/rl/public/completed-matches';
        const res = await fetch(url);
        const completedMatches = await res.json();
        setMatches(completedMatches);
        setFilteredMatches(completedMatches);
      } catch (error) {
        console.error("Error loading completed matches:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMatches();
  }, [selectedDate]);

  // Handle filter changes
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    applyFilters(filter);
  };

  // Handle date change from the DatePicker
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      localStorage.setItem('completedMatchesDate', date.toISOString());
    } else {
      localStorage.removeItem('completedMatchesDate');
    }
    //setFilteredMatches(filtered);
  };


  // Combined filtering logic
  const applyFilters = (leagueFilter) => {
    let filtered = matches;

    if (leagueFilter !== 'all') {
      filtered = filtered.filter(match =>
        match.league.toLowerCase().includes(leagueFilter.toLowerCase())
      );
    }
    setFilteredMatches(filtered);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Seo
        title="Rocket League Results & Scores"
        description="Latest Rocket League esports results and final scores — RLCS, regionals, and majors. Browse completed matches with full game-by-game breakdowns."
        path="/completed-matches"
        schemas={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Results', path: '/completed-matches' }])]}
      />
      <div className="page-header">
        <button onClick={handleBack} className="back-link">← Back</button>
        <h1>Results</h1>
      </div>

      <div className="matches-filters">
        <button
          className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
        </button>
        <button
          className={`filter-btn ${activeFilter === 'rlcs' ? 'active' : ''}`}
          onClick={() => handleFilterChange('rlcs')}
        >
          RLCS
        </button>
        <button
          className={`filter-btn ${activeFilter === 'regional' ? 'active' : ''}`}
          onClick={() => handleFilterChange('regional')}
        >
          Regional
        </button>
        <button
          className={`filter-btn ${activeFilter === 'major' ? 'active' : ''}`}
          onClick={() => handleFilterChange('major')}
        >
          Major
        </button>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className="date-picker-container">
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              maxDate={new Date()}
              slotProps={{
                textField: { size: 'small', label: '', placeholder: 'Filter by date' },
                // Tags the portaled popper so admin stylesheets can't repaint it
                popper: { className: 'jolt-calendar' },
              }}
            />
            {selectedDate && (
              <button
                className="reset-date-btn"
                onClick={() => {
                  setSelectedDate(null);
                  localStorage.removeItem('completedMatchesDate');
                }}
              >
                ×
              </button>
            )}
          </div>
        </LocalizationProvider>
      </div>

      <MatchesList
        matches={filteredMatches}
        isCompleted={true}
        isLoading={isLoading}
        matchDate={formatDateToISO(selectedDate)}
      />
    </>
  );
};

export default CompletedMatches;
